import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import Modal from '../components/common/Modal';

const BUCKET_META = {
  '0-30':  { label: '0–30 days',  bg: '#d1fae5', color: '#065f46' },
  '31-60': { label: '31–60 days', bg: '#fef3c7', color: '#92400e' },
  '61-90': { label: '61–90 days', bg: '#ffedd5', color: '#c2410c' },
  '90+':   { label: '90+ days',   bg: '#fee2e2', color: '#dc2626' },
};

const fmt = (n) => `₹${parseFloat(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

export default function Receivables() {
  const [data, setData]         = useState([]);
  const [summary, setSummary]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [bucketFilter, setBucket] = useState('');
  const [search, setSearch]     = useState('');
  const [detail, setDetail]     = useState(null);   // { client, invoices }
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get('/accounting/receivables');
      setData(res.data || []);
      setSummary(res.summary || null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (client) => {
    setDetail({ client, invoices: [] });
    setDetailLoading(true);
    try {
      const { data: res } = await api.get(`/accounting/receivables/${client.client_id}/invoices`);
      setDetail({ client, invoices: res.data || [] });
    } finally { setDetailLoading(false); }
  };

  const filtered = data.filter(r => {
    if (bucketFilter && r.aging_bucket !== bucketFilter) return false;
    if (search) {
      const s = search.toLowerCase();
      return r.client_name.toLowerCase().includes(s) || (r.city || '').toLowerCase().includes(s);
    }
    return true;
  });

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Accounts Receivable</h1>
          <p className="page-subtitle">Outstanding invoices and client payment status</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary no-print" onClick={load}>Refresh</button>
          <button className="btn btn-secondary no-print" onClick={() => window.print()}>
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
            Print
          </button>
        </div>
      </div>

      {/* Summary cards */}
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 20 }}>
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '16px 20px' }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 6 }}>Total Invoiced</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#0f172a' }}>{fmt(summary.total_invoiced)}</div>
            <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 3 }}>{summary.total_clients} clients</div>
          </div>
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '16px 20px' }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 6 }}>Total Paid</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#059669' }}>{fmt(summary.total_paid)}</div>
            <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 3 }}>
              {summary.total_invoiced ? `${Math.round(summary.total_paid / summary.total_invoiced * 100)}% collected` : '—'}
            </div>
          </div>
          <div style={{ background: '#fff', border: '1px solid #fee2e2', borderRadius: 12, padding: '16px 20px', borderLeft: '3px solid #dc2626' }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 6 }}>Outstanding</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#dc2626' }}>{fmt(summary.total_outstanding)}</div>
            <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 3 }}>
              {summary.total_invoiced ? `${Math.round(summary.total_outstanding / summary.total_invoiced * 100)}% unpaid` : '—'}
            </div>
          </div>
          {/* Progress bar */}
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '16px 20px' }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 8 }}>Collection Rate</div>
            <div style={{ height: 8, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden', marginBottom: 10 }}>
              <div style={{ height: '100%', background: '#10b981', borderRadius: 4, width: summary.total_invoiced ? `${Math.min(100, summary.total_paid / summary.total_invoiced * 100)}%` : '0%', transition: 'width 0.4s' }} />
            </div>
            <div style={{ fontSize: '0.72rem', color: '#64748b' }}>
              {fmt(summary.total_paid)} of {fmt(summary.total_invoiced)}
            </div>
          </div>
        </div>
      )}

      {/* Aging buckets */}
      {summary?.aging && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 20 }}>
          {Object.entries(BUCKET_META).map(([key, meta]) => (
            <div key={key} onClick={() => setBucket(bucketFilter === key ? '' : key)}
              style={{ padding: '10px 14px', background: bucketFilter === key ? meta.bg : '#fff', border: `1px solid ${bucketFilter === key ? meta.color : '#e2e8f0'}`, borderRadius: 10, cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s' }}>
              <div style={{ fontSize: '1.1rem', fontWeight: 800, color: meta.color }}>{fmt(summary.aging[key])}</div>
              <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#64748b', marginTop: 2 }}>{meta.label}</div>
              <div style={{ fontSize: '0.68rem', color: '#94a3b8', marginTop: 1 }}>
                {data.filter(r => r.aging_bucket === key).length} clients
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Search */}
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-body" style={{ padding: '12px 16px' }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <div className="search-input-wrap" style={{ flex: 1 }}>
              <span className="search-icon">
                <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              </span>
              <input className="form-control" placeholder="Search client or city…"
                value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            {(search || bucketFilter) && (
              <button className="btn btn-ghost btn-sm" onClick={() => { setSearch(''); setBucket(''); }}>Clear</button>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Invoices</th>
                <th style={{ textAlign: 'right' }}>Invoiced</th>
                <th style={{ textAlign: 'right' }}>Paid</th>
                <th style={{ textAlign: 'right' }}>Outstanding</th>
                <th>Aging</th>
                <th>Oldest Invoice</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8}><div className="loading-overlay"><div className="spinner" /></div></td></tr>}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={8}><div className="empty-state"><p>{data.length === 0 ? 'No outstanding receivables.' : 'No results match your filter.'}</p></div></td></tr>
              )}
              {!loading && filtered.map(r => {
                const bm = BUCKET_META[r.aging_bucket] || BUCKET_META['0-30'];
                const collectionPct = r.total_invoiced ? Math.round(r.total_paid / r.total_invoiced * 100) : 0;
                return (
                  <tr key={r.client_id}>
                    <td>
                      <div style={{ fontWeight: 700, fontSize: '0.88rem' }}>{r.client_name}</div>
                      {r.city && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{r.city}{r.country ? `, ${r.country}` : ''}</div>}
                    </td>
                    <td style={{ textAlign: 'center', fontWeight: 600 }}>{r.invoice_count}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(r.total_invoiced)}</td>
                    <td style={{ textAlign: 'right', color: '#059669', fontWeight: 600 }}>
                      {fmt(r.total_paid)}
                      <div style={{ height: 3, background: '#e2e8f0', borderRadius: 2, marginTop: 3, overflow: 'hidden' }}>
                        <div style={{ height: '100%', background: '#10b981', width: `${collectionPct}%` }} />
                      </div>
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 800, color: '#dc2626' }}>{fmt(r.outstanding)}</td>
                    <td>
                      <span style={{ padding: '3px 10px', borderRadius: 12, background: bm.bg, color: bm.color, fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {bm.label}
                      </span>
                    </td>
                    <td style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{fmtDate(r.oldest_invoice_date)}</td>
                    <td>
                      <button className="btn btn-ghost btn-sm" onClick={() => openDetail(r)}>
                        View Invoices
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!loading && filtered.length > 0 && (
          <div style={{ padding: '10px 20px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 24, fontSize: '0.82rem' }}>
            <span>Showing <strong>{filtered.length}</strong> clients</span>
            <span>Outstanding: <strong style={{ color: '#dc2626' }}>{fmt(filtered.reduce((s, r) => s + parseFloat(r.outstanding || 0), 0))}</strong></span>
          </div>
        )}
      </div>

      {/* Client invoices detail modal */}
      {detail && (
        <Modal open onClose={() => setDetail(null)}
          title={`Outstanding Invoices — ${detail.client.client_name}`} size="lg"
          footer={<button className="btn btn-secondary" onClick={() => setDetail(null)}>Close</button>}
        >
          {detailLoading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" /></div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
                {[
                  ['Invoiced', fmt(detail.client.total_invoiced), '#0f172a'],
                  ['Paid', fmt(detail.client.total_paid), '#059669'],
                  ['Outstanding', fmt(detail.client.outstanding), '#dc2626'],
                ].map(([l, v, c]) => (
                  <div key={l} style={{ flex: 1, minWidth: 100, background: '#f8fafc', borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
                    <div style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>{l}</div>
                    <div style={{ fontSize: '1.1rem', fontWeight: 800, color: c }}>{v}</div>
                  </div>
                ))}
              </div>
              <table className="table">
                <thead>
                  <tr>
                    <th>Invoice #</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'right' }}>Total</th>
                    <th style={{ textAlign: 'right' }}>Paid</th>
                    <th style={{ textAlign: 'right' }}>Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.invoices.length === 0 && (
                    <tr><td colSpan={6} style={{ textAlign: 'center', color: '#94a3b8', padding: 20 }}>No outstanding invoices</td></tr>
                  )}
                  {detail.invoices.map(inv => (
                    <tr key={inv.id}>
                      <td style={{ fontWeight: 700, color: 'var(--primary)', fontSize: '0.82rem' }}>{inv.order_number}</td>
                      <td style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{fmtDate(inv.created_at)}</td>
                      <td><span className="badge badge-neutral" style={{ fontSize: '0.7rem', textTransform: 'capitalize' }}>{inv.status}</span></td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(inv.total)}</td>
                      <td style={{ textAlign: 'right', color: '#059669' }}>{fmt(inv.paid)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 800, color: '#dc2626' }}>{fmt(inv.balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </Modal>
      )}
    </div>
  );
}
